/**
 * tasks-new.ts
 * 
 * Servizio per la gestione dei task tramite API.
 * Allineato con le API del backend UfficioStile.
 */

import { apiClient, buildQueryParams } from './base';
import {
  ProjectTask,
  ProjectTaskCreateRequest,
  ProjectTaskUpdateRequest,
  TaskStatus,
  TaskPriority,
  ApiResponse,
  PagedResponse,
  SearchParams,
  ProjectTaskDetail,
  CreateKanbanTaskRequest,
  UpdateKanbanTaskRequest,
  TaskAttachment,
} from '../../types/api';

export class TaskService {
  private baseUrl = '/api/ProjectTask';

  // ================== OPERAZIONI CRUD BASE ==================
  async getAllTasks(params?: SearchParams): Promise<PagedResponse<ProjectTask>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    const response = await apiClient.get<PagedResponse<ProjectTask>>(url);
    return response.data;
  }

  async getTaskById(id: string): Promise<ProjectTask> {
    const response = await apiClient.get<ProjectTask>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async createTask(data: ProjectTaskCreateRequest): Promise<ProjectTask> {
    const response = await apiClient.post<ProjectTask>(this.baseUrl, data);
    return response.data;
  }

  async updateTask(id: string, data: ProjectTaskUpdateRequest): Promise<ProjectTask> {
    const response = await apiClient.put<ProjectTask>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async deleteTask(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  // ================== OPERAZIONI KANBAN ==================
  async getKanbanBoard(): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/kanban-board`);
    return response.data;
  }

  async getTasksByColumn(column: number): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/by-column/${column}`);
    return response.data;
  }

  async moveTaskToColumn(id: string, column: number): Promise<void> {
    const data = { column };
    await apiClient.post(`${this.baseUrl}/${id}/move-to-column`, data);
  }

  async reorderTasks(tasks: { id: string; position: number }[]): Promise<void> {
    await apiClient.post(`${this.baseUrl}/reorder`, { tasks });
  }

  // ================== OPERAZIONI STATO ==================
  async updateTaskStatus(id: string, newStatus: TaskStatus, notes?: string): Promise<void> {
    const data = { status: newStatus, notes };
    await apiClient.post(`${this.baseUrl}/${id}/status`, data);
  }

  async completeTask(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/complete`);
  }

  async reopenTask(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/reopen`);
  }

  // ================== OPERAZIONI ASSEGNAZIONE ==================
  async assignTask(id: string, userId: string): Promise<void> {
    const data = { userId };
    await apiClient.post(`${this.baseUrl}/${id}/assign`, data);
  }

  async unassignTask(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/unassign`);
  }

  async getTasksAssignedToUser(userId: string): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/assigned-to/${userId}`);
    return response.data;
  }

  async getMyTasks(): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/my-tasks`);
    return response.data;
  }

  async getUnassignedTasks(): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/unassigned`);
    return response.data;
  }

  // ================== OPERAZIONI FILTRI ==================
  async getTasksByRequest(requestId: string): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/by-request/${requestId}`);
    return response.data;
  }

  async getTasksByPriority(priority: TaskPriority): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/by-priority/${priority}`);
    return response.data;
  }

  async getHighPriorityTasks(): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/high-priority`);
    return response.data;
  }

  async getOverdueTasks(): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/overdue`);
    return response.data;
  }

  async getTasksDueSoon(): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/due-soon`);
    return response.data;
  }

  // ================== OPERAZIONI RICERCA ==================
  async searchTasks(params: {
    searchTerm?: string;
    status?: TaskStatus;
    priority?: TaskPriority;
    assignedUserId?: string;
    requestId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PagedResponse<ProjectTask>> {
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<PagedResponse<ProjectTask>>(`${this.baseUrl}/search?${queryString}`);
    return response.data;
  }

  // ================== STATISTICHE ==================
  async getTaskStatistics(): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/statistics`);
    return response.data;
  }

  // ================== OPERAZIONI UTILITÀ ==================
  async duplicateTask(id: string): Promise<ProjectTask> {
    const response = await apiClient.post<ProjectTask>(`${this.baseUrl}/${id}/duplicate`);
    return response.data;
  }

  // ================== KANBAN PLANNER: dettaglio e CRUD tipizzati ==================
  // NOTA: `completeTask`/`reopenTask` sopra (id: string, nessun ritorno) sono i metodi legacy
  // della Kanban a colonne-stato. Qui sotto gli stessi nomi sono usati (per allineamento con
  // il backend) con firma diversa (id numerico, ritorno ProjectTaskDetail): la board Planner
  // dinamica userà sempre questi. Non sono raggiungibili in overload nella stessa classe, quindi
  // le vecchie API restano sopra e sono usate solo da `use-tasks.ts` (nessun consumatore reale oggi).

  async getTaskDetail(id: number): Promise<ProjectTaskDetail> {
    const response = await apiClient.get<ProjectTaskDetail>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async createKanbanTask(dto: CreateKanbanTaskRequest): Promise<ProjectTaskDetail> {
    const response = await apiClient.post<ProjectTaskDetail>(this.baseUrl, dto);
    return response.data;
  }

  async updateKanbanTask(id: number, dto: UpdateKanbanTaskRequest): Promise<ProjectTaskDetail> {
    const response = await apiClient.put<ProjectTaskDetail>(`${this.baseUrl}/${id}`, dto);
    return response.data;
  }

  async completeKanbanTask(id: number, completedAt?: string): Promise<ProjectTaskDetail> {
    const response = await apiClient.post<ProjectTaskDetail>(`${this.baseUrl}/${id}/complete`, { completedAt });
    return response.data;
  }

  async reopenKanbanTask(id: number): Promise<ProjectTaskDetail> {
    const response = await apiClient.post<ProjectTaskDetail>(`${this.baseUrl}/${id}/reopen`);
    return response.data;
  }

  async getAttachments(id: number): Promise<TaskAttachment[]> {
    const response = await apiClient.get<TaskAttachment[]>(`${this.baseUrl}/${id}/attachments`);
    return response.data;
  }

  /**
   * Carica un allegato. Usa `apiClient.upload` (fetch, senza timeout) e non l'istanza axios
   * a 10-30s usata altrove: gli allegati (fino a 50MB) possono superare quella soglia.
   */
  async uploadAttachment(
    id: number,
    file: File,
    thumbnail?: File | null,
    setAsCover?: boolean
  ): Promise<TaskAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }
    formData.append('setAsCover', String(!!setAsCover));

    const response = await apiClient.upload<TaskAttachment>(`${this.baseUrl}/${id}/attachments`, formData);
    return response.data;
  }

  async deleteAttachment(id: number, attachmentId: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}/attachments/${attachmentId}`);
  }

  async setCover(id: number, attachmentId: number | null): Promise<TaskAttachment | null> {
    const response = await apiClient.put<TaskAttachment | null>(`${this.baseUrl}/${id}/cover`, { attachmentId });
    return response.data;
  }
}

export const taskService = new TaskService();
